
import { api } from "@/api";
import { clipsApi } from "@/api/clips";
import { derivativesApi } from "@/api/derivatives";

// Tracking memory to prevent duplicate view counts in the same session
const viewedItems = new Set<string>();

// Utility function for running background tasks
const runInBackground = (task: () => Promise<any>) => {
  try {
    // @ts-ignore - EdgeRuntime is available in edge functions
    EdgeRuntime.waitUntil(task());
  } catch (e) {
    // Fallback for non-edge environments
    task().catch(console.error);
  }
};

// Function to track clip views
export const trackClipView = (clipId: string): Promise<boolean> => {
  console.log(`Tracking view for clip: ${clipId}`);
  
  // Skip if already viewed in this session
  if (viewedItems.has(`clip-${clipId}`)) {
    console.log(`Clip ${clipId} already viewed in this session, skipping`);
    return Promise.resolve(false);
  }
  
  // Mark as viewed
  viewedItems.add(`clip-${clipId}`);
  
  return new Promise((resolve) => {
    // Run view tracking in background
    runInBackground(async () => {
      try {
        // Use the clipsApi directly instead of api.clips
        const result = await clipsApi.incrementClipView(clipId);
        console.log("View tracking result:", result);
        return result;
      } catch (error) {
        console.error("Error tracking clip view:", error);
        return false;
      }
    });
    
    // Return immediately with success
    resolve(true);
  });
};

// Function to track clip downloads (alias for recordClipDownload)
export const recordClipDownload = (clipId: string, clipTitle: string, userId?: string): Promise<boolean> => {
  console.log(`Tracking download for clip: ${clipId} by user ${userId || 'anonymous'}`);
  
  return new Promise((resolve) => {
    // Run download tracking in background
    runInBackground(async () => {
      try {
        // Use the clipsApi directly instead of api.clips
        const result = await clipsApi.recordClipDownload(clipId, clipTitle, userId);
        console.log("Download tracking result:", result);
        return result;
      } catch (error) {
        console.error("Error tracking clip download:", error);
        return false;
      }
    });
    
    // Return immediately with success
    resolve(true);
  });
};

// Function to increment playlist views
export const incrementPlaylistViews = (playlistId: string): Promise<boolean> => {
  if (!playlistId) {
    console.warn("No playlist ID provided for view increment");
    return Promise.resolve(false);
  }
  
  // Skip if already viewed in this session
  if (viewedItems.has(`playlist-${playlistId}`)) {
    console.log(`Playlist ${playlistId} already viewed in this session, skipping`);
    return Promise.resolve(false);
  }
  
  // Mark as viewed
  viewedItems.add(`playlist-${playlistId}`);
  
  console.log(`Incrementing views for playlist: ${playlistId}`);
  
  return new Promise((resolve) => {
    // Run playlist view increment in background
    runInBackground(async () => {
      try {
        const result = await api.playlists.incrementPlaylistView(playlistId);
        console.log("Playlist view increment result:", result);
        return result;
      } catch (error) {
        console.error("Error incrementing playlist views:", error);
        return false;
      }
    });
    
    // Return immediately with success
    resolve(true);
  });
};

// Function to increment profile views 
export const incrementProfileView = (profileId: string): Promise<boolean> => {
  if (!profileId) {
    console.warn("No profile ID provided for view increment");
    return Promise.resolve(false);
  }
  
  // Skip if already viewed in this session
  if (viewedItems.has(`profile-${profileId}`)) {
    console.log(`Profile ${profileId} already viewed in this session, skipping`);
    return Promise.resolve(false);
  }
  
  // Mark as viewed
  viewedItems.add(`profile-${profileId}`);
  
  console.log(`Incrementing views for profile: ${profileId}`);
  
  return new Promise((resolve) => {
    // Run profile view increment in background
    runInBackground(async () => {
      try {
        const result = await api.creators.incrementProfileView(profileId);
        console.log("Profile view increment result:", result);
        return result;
      } catch (error) {
        console.error("Error incrementing profile views:", error);
        return false;
      }
    });
    
    // Return immediately with success
    resolve(true);
  });
};

// Alias for backward compatibility
export const incrementProfileViews = incrementProfileView;

// Function to increment derivative views
export const incrementDerivativeView = (derivativeId: string): Promise<boolean> => {
  if (!derivativeId) {
    console.warn("No derivative ID provided for view increment");
    return Promise.resolve(false);
  }
  
  // Skip if already viewed in this session
  if (viewedItems.has(`derivative-${derivativeId}`)) {
    console.log(`Derivative ${derivativeId} already viewed in this session, skipping`);
    return Promise.resolve(false);
  }
  
  // Mark as viewed
  viewedItems.add(`derivative-${derivativeId}`);
  
  console.log(`Incrementing views for derivative: ${derivativeId}`);
  
  return new Promise((resolve) => {
    // Run derivative view increment in background
    runInBackground(async () => {
      try {
        // Use derivativesApi directly
        const result = await derivativesApi.incrementDerivativeView(derivativeId);
        console.log("Derivative view increment result:", result);
        return result;
      } catch (error) {
        console.error("Error incrementing derivative views:", error);
        return false;
      }
    });
    
    // Return immediately with success
    resolve(true);
  });
};
